import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { ThemeService } from 'freyr-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from './core/services/auth.service';
import { ApiService, TwitchScopeStatus } from './services/api.service';
import { TopNavComponent } from './layout/top-nav/top-nav.component';
import { UiBackdropComponent } from 'freyr-ui';
import { LoaderComponent } from './layout/loader/loader.component';
import { UiFooterComponent } from 'freyr-ui';
import { UiToastItem, UiToastService } from 'freyr-ui';
import {BuildVersionInfo, BuildVersionService} from './core/services/build-version.service';
import {SiteAnalyticsService} from './core/services/site-analytics.service';

const LANG_KEY = 'freyr-lang';

@Component({
    selector: 'app-root',
    imports: [CommonModule, RouterOutlet, TopNavComponent, UiBackdropComponent, LoaderComponent, UiFooterComponent, TranslateModule],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'Freyr';
  showFooter = false;
  toasts: UiToastItem[] = [];
  availableUpdate: BuildVersionInfo | null = null;
  twitchScopeStatus: TwitchScopeStatus | null = null;

  constructor(
    private themeService: ThemeService,
    private translate: TranslateService,
    private router: Router,
    private api: ApiService,
    private auth: AuthService,
    private toastService: UiToastService,
    private buildVersionService: BuildVersionService,
    private siteAnalyticsService: SiteAnalyticsService
  ) {
    this.translate.setDefaultLang('fr');
    const saved = localStorage.getItem(LANG_KEY);
    if (saved && ['fr', 'en'].includes(saved)) {
      this.translate.use(saved);
    } else {
      const browser = this.translate.getBrowserLang();
      const lang = browser === 'en' ? 'en' : 'fr';
      this.translate.use(lang);
      localStorage.setItem(LANG_KEY, lang);
    }
  }

  ngOnInit(): void {
    this.siteAnalyticsService.start();
    this.toastService.toasts$.subscribe((toasts: UiToastItem[]) => {
      this.toasts = toasts;
    });
    this.buildVersionService.availableUpdate$.subscribe((buildInfo) => {
      this.availableUpdate = buildInfo;
    });

    this.updateFooterVisibility();
    // Le loader global est géré par loaderInterceptor (finalize sur chaque requête HTTP).
    // Ne pas l’associer à NavigationStart/End : une garde qui appelle l’API (ex. GET /me) incrémente
    // déjà le compteur ; NavigationCancel/End ne correspond pas 1:1 aux requêtes → risque de spinner bloqué.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.updateFooterVisibility());

    this.api.getCurrentUser(undefined, { skipDelegation: true }).subscribe({
      next: (user) => {
        this.auth.setUser(user);
        this.loadTwitchScopeStatus();
      },
      error: () => {
        this.auth.setUser(null);
        this.twitchScopeStatus = null;
      }
    });
  }

  private updateFooterVisibility(): void {
    const url = this.router.url.split('?')[0];
    this.showFooter = url !== '' && url !== '/';
  }

  dismissToast(id: number): void {
    this.toastService.dismiss(id);
  }

  trackToast(_index: number, toast: UiToastItem): number {
    return toast.id;
  }

  refreshForUpdate(): void {
    this.buildVersionService.reloadPage();
  }

  reconnectTwitch(): void {
    this.api.getTwitchLoginUrl().subscribe({
      next: (response) => {
        if (response?.url) {
          window.location.href = response.url;
        }
      },
      error: () => {}
    });
  }

  private loadTwitchScopeStatus(): void {
    if (!this.auth.user) {
      this.twitchScopeStatus = null;
      return;
    }
    this.api.getTwitchScopes({ skipDelegation: true }).subscribe({
      next: (status) => {
        this.twitchScopeStatus = status?.scopeChangeRequired ? status : null;
      },
      error: () => {
        this.twitchScopeStatus = null;
      }
    });
  }

  get footerCopyright(): string {
    return this.translate.instant('home.footer.copyright', { year: '{{year}}' });
  }

  get footerCguLabel(): string {
    return this.translate.instant('home.footer.cgu');
  }

  get footerPrivacyLabel(): string {
    return this.translate.instant('home.footer.privacy');
  }

  get updateBannerTitle(): string {
    return this.translate.instant('app.versionUpdate.title');
  }

  get updateBannerMessage(): string {
    return this.translate.instant('app.versionUpdate.message', {
      version: this.availableUpdate?.version ?? '',
      buildId: this.availableUpdate?.buildId ?? ''
    });
  }

  get updateBannerAction(): string {
    return this.translate.instant('app.versionUpdate.action');
  }

  get scopeBannerTitle(): string {
    return this.translate.instant('app.twitchScopesUpdate.title');
  }

  get scopeBannerMessage(): string {
    const missingCount = this.twitchScopeStatus?.missingScopes?.length ?? 0;
    const deprecatedCount = this.twitchScopeStatus?.deprecatedScopes?.length ?? 0;
    return this.translate.instant('app.twitchScopesUpdate.message', { missingCount, deprecatedCount });
  }

  get scopeBannerAction(): string {
    return this.translate.instant('app.twitchScopesUpdate.action');
  }
}

export function getStoredLang(): string {
  return localStorage.getItem(LANG_KEY) || 'fr';
}

export function setStoredLang(lang: string): void {
  localStorage.setItem(LANG_KEY, lang);
}



